import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { api, type Health } from "@/lib/api";
import { useConnectionState } from "@/hooks/useConnection";
import { formatDateTime, formatUptime } from "@/lib/format";
import { cn } from "@/lib/utils";

const DOT: Record<string, string> = {
  ok: "bg-success",
  degraded: "bg-yellow-500",
  down: "bg-destructive",
  disabled: "bg-muted-foreground/40",
};

/**
 * The collapsed summary's render order, and the one place a state's colour and its word live
 * together. `ok` always renders — "green 5" is the entire point of the collapsed row — and
 * the rest appear only when non-zero, so a healthy system is one dot and one number.
 */
const SUMMARY = [
  { state: "ok", label: "healthy", dot: "bg-success", text: "text-success" },
  { state: "degraded", label: "degraded", dot: "bg-yellow-500", text: "text-yellow-500" },
  { state: "down", label: "down", dot: "bg-destructive", text: "text-destructive" },
  { state: "disabled", label: "disabled", dot: "bg-muted-foreground/40", text: "text-muted-foreground/70" },
] as const;

/**
 * Counts by state, off the same `checks` object the expanded list renders from, so the number
 * and the list can never disagree. Seeded at zero so "hide when zero" is one comparison.
 *
 * An unrecognised state from a future server increments its own key and is left out of the
 * summary — but it still shows up in the expanded list and, if it has a detail, in the
 * paragraphs, so a check this file has never heard of cannot vanish silently.
 */
function countByState(checks: Health["checks"]): Record<string, number> {
  const counts: Record<string, number> = { ok: 0, degraded: 0, down: 0, disabled: 0 };
  for (const check of Object.values(checks)) {
    counts[check.state] = (counts[check.state] ?? 0) + 1;
  }
  return counts;
}

/**
 * W4.1's visible half. Everything on every screen is a React Query cache, and a cache with a
 * dead socket behind it renders the last good answer forever — so "live" has to be something
 * you can see, not something you infer from numbers that happen to be moving.
 */
function LiveDot() {
  const state = useConnectionState();
  if (state === "open") {
    return (
      <span className="flex items-center gap-1.5 text-success">
        <span className="h-2 w-2 rounded-full bg-success" />
        Live
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-yellow-500">
      <span className="h-2 w-2 rounded-full bg-yellow-500" />
      {state === "connecting" ? "Reconnecting" : "Not live"}
    </span>
  );
}

export function HealthStrip() {
  // Above the early returns, not next to the JSX that uses it: hooks cannot sit behind a
  // conditional return. Survives the 30s refetch, because the component does not remount.
  const [open, setOpen] = useState(false);
  const { data, isError } = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 30_000,
  });

  if (isError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
        <span className="h-2 w-2 rounded-full bg-destructive" />
        Cannot reach the server.
      </div>
    );
  }

  // The socket indicator renders even before health has answered: a screen with no
  // connection is exactly when the first request is least likely to come back.
  if (!data) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-card/50 px-3 py-2 text-xs text-muted-foreground">
        <LiveDot />
      </div>
    );
  }

  const entries = Object.entries(data.checks);
  const counts = countByState(data.checks);
  const shown = SUMMARY.filter(({ state }) => state === "ok" || (counts[state] ?? 0) > 0);
  const problems = entries.filter(
    ([, check]) => check.state !== "ok" && check.state !== "disabled" && check.detail,
  );

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-card/50 px-3 py-2 text-xs text-muted-foreground">
      {/* Six named dots left no room for the one thing the strip gets asked from a phone —
          "is the box running my last deploy" — and the names were never the question anyway.
          "Is anything wrong, and how much" is, so that is what the collapsed row answers.
          The names are one tap away. The REASONS are not: see below. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? "health-detail" : undefined}
        // Padded for a thumb, then pulled back so the strip keeps the height it had.
        className="-my-1.5 flex items-center gap-2 rounded-sm py-1.5 pr-1 outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {shown.map(({ state, label, dot, text }) => (
          <span key={state} className={cn("flex items-center gap-1.5 tabular-nums", text)}>
            <span className={cn("h-2 w-2 rounded-full", dot)} />
            {counts[state] ?? 0}
            {/* Colour is not a label. This also gives the button its accessible name. */}
            <span className="sr-only"> {label}</span>
          </span>
        ))}
        <ChevronDown
          className={cn("h-3 w-3 text-muted-foreground/70 transition-transform", open && "rotate-180")}
          aria-hidden="true"
        />
        <span className="sr-only">{open ? "Hide" : "Show"} health details</span>
      </button>
      <LiveDot />
      {/* The build, not the version: `version` has been 0.1.0 since the first commit, so it
          could never answer the question it appeared to be answering. */}
      <span className="ml-auto font-mono text-muted-foreground/70">{data.sha}</span>
      {/* A yellow or red dot must explain itself; the reason was always in the JSON but
          never on screen. Own row via w-full inside the flex-wrap — phones have no hover.
          These deliberately stay OUT of the disclosure: a reason you have to go looking for
          is the same as no reason. Only the names and the build info moved behind the tap. */}
      {problems.map(([name, check]) => (
        <p
          key={`${name}-detail`}
          className={cn(
            "w-full text-[11px]",
            check.state === "down" ? "text-destructive" : "text-yellow-500",
          )}
        >
          <span className="capitalize">{name}</span>: {check.detail}
        </p>
      ))}
      {open && (
        <div
          id="health-detail"
          className="max-h-64 w-full space-y-2 overflow-y-auto border-t border-border pt-2"
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {entries.map(([name, check]) => (
              <span key={name} className="flex items-center gap-1.5">
                <span className={cn("h-2 w-2 rounded-full", DOT[check.state] ?? "bg-muted-foreground/40")} />
                <span className="capitalize">{name}</span>
              </span>
            ))}
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground/70">
            <dt>Version</dt>
            <dd>v{data.version}</dd>
            <dt>Started</dt>
            <dd>{formatDateTime(data.startedAt)}</dd>
            <dt>Uptime</dt>
            <dd>{formatUptime(data.uptimeSeconds)}</dd>
          </dl>
        </div>
      )}
    </div>
  );
}
